import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { PencilIcon, Trash2Icon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import type { EntryValue } from '@/lib/api/credit-conversion-settings';
import { formatNumber } from '@/lib/credit-conversion-settings/number';
import type { SettingItem, TableDef } from '@/lib/credit-conversion-settings/tables';

import { CategoryChip } from './EntryListPanel';
import { columnWidthSx, fitContentSx, tableSx } from './tableLayout';

export interface ValueViewProps {
  item: SettingItem;
  value: EntryValue;
  busy: boolean;
  /** 削除できたかが分からない旨を表示した後。もう一度の削除と「再読み込み」だけができる */
  frozen: boolean;
  onEdit: () => void;
  onDelete: () => void;
}

/** 消費項目の日本語。画面が知らない消費項目は値のまま表示する */
export function consumptionItemLabel(value: EntryValue | undefined, item: string): string {
  return value?.consumption_items?.find((i) => i.value === item)?.label ?? item;
}

/** 表示モード。予定の値のときだけ「編集」「削除」を置く */
export function ValueView({ item, value, busy, frozen, onEdit, onDelete }: ValueViewProps) {
  return (
    <Box>
      <Box display="flex" alignItems="center" gap={1.5} mb={1.5} flexWrap="wrap">
        <Typography variant="subtitle1" fontWeight="bold">
          適用開始日 {value.effective_dt}
        </Typography>
        <CategoryChip category={value.category} />
        {value.category === 'scheduled' && (
          <Box display="flex" gap={1} ml="auto">
            <Button size="sm" variant="outline" onClick={onEdit} disabled={busy || frozen}>
              <PencilIcon />
              編集
            </Button>
            <Button size="sm" variant="destructive" onClick={onDelete} disabled={busy}>
              <Trash2Icon />
              削除
            </Button>
          </Box>
        )}
      </Box>
      {value.category !== 'scheduled' && (
        <Typography variant="body2" color="text.secondary" mb={1.5}>
          適用中と過去の値は変更できません。値を変えるときは「追加」で、当日より後の適用開始日の値を作ってください。
        </Typography>
      )}
      <Box display="flex" flexDirection="column" gap={2}>
        {item.tables.map((table) => (
          <RowsView
            key={table.name}
            table={table}
            value={value}
            label={item.tables.length > 1 ? table.label : undefined}
          />
        ))}
      </Box>
    </Box>
  );
}

/** 1つのテーブルの行の一覧。設定項目のテーブルが2つあるときは、読み上げで区別できるよう、aria-labelをテーブルの名前にする */
function RowsView({ table, value, label }: { table: TableDef; value: EntryValue; label?: string }) {
  const rows = value.tables.find((t) => t.table === table.name)?.rows ?? [];
  return (
    <Box>
      <TableContainer sx={fitContentSx}>
        <Table size="small" aria-label={label ?? '値'} sx={tableSx}>
          <TableHead>
            <TableRow>
              {table.columns.map((c) => (
                <TableCell
                  key={c.name}
                  align={c.kind === 'number' ? 'right' : 'left'}
                  sx={columnWidthSx(c)}
                >
                  {c.header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, i) => (
              <TableRow key={i}>
                {table.columns.map((c) => (
                  <TableCell
                    key={c.name}
                    align={c.kind === 'number' ? 'right' : 'left'}
                    sx={{ fontVariantNumeric: 'tabular-nums' }}
                  >
                    {c.kind === 'number'
                      ? formatNumber(row[c.name] ?? '')
                      : consumptionItemLabel(value, row[c.name] ?? '')}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
